import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TimerOffIcon from "@mui/icons-material/TimerOff";
import LockIcon from "@mui/icons-material/Lock";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import moment from "moment";
import { Shell } from "../../common/shell/shell.page";
import { LoadingOverlay } from "../../common/components/loading-overlay";
import { useCashRegister } from "../cash-register-context";
import { buildCashRegisterSummary } from "../cash-register-summary";
import { CashRegisterReportView } from "../components/cash-register-report-view";
import { CashRegisterReportHero } from "../components/cash-register-report-hero";
import { CashMovementsLogView } from "../components/cash-movements-log-view";
import application from "../../core/application";
import { MoneyInput } from "../../common/components/money-input";

const money = (n: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n);

export function CashRegisterClosePage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [user] = application.user.useState();
  const {
    featureEnabled,
    loading,
    session,
    sessionExpired,
    canManage,
    closeSession,
    movements,
    sells,
  } = useCashRegister();

  const [closingValue, setClosingValue] = useState(0);
  const [closingTouched, setClosingTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!featureEnabled) {
      navigate("/", { replace: true });
    }
  }, [featureEnabled, navigate]);

  useEffect(() => {
    if (loading) return;
    if (!session) {
      navigate("/", { replace: true });
      return;
    }
    if (!sessionExpired) {
      navigate("/caixa", { replace: true });
    }
  }, [loading, session, sessionExpired, navigate]);

  const baseSummary = useMemo(() => {
    if (!session) return null;
    return buildCashRegisterSummary(session, sells, movements);
  }, [session, sells, movements]);

  const previewSummary = useMemo(() => {
    if (!baseSummary) return null;
    if (!closingTouched) return baseSummary;
    return {
      ...baseSummary,
      closingCash: closingValue,
      differenceApprox: closingValue - baseSummary.expectedCashApprox,
    };
  }, [baseSummary, closingValue, closingTouched]);

  const onSubmit = async () => {
    if (!closingTouched) {
      setError("Informe o valor em dinheiro contado no caixa.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await closeSession(closingValue);
      navigate("/caixa", { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível fechar o caixa.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!featureEnabled || loading || !session || !sessionExpired) {
    return (
      <Shell title="Caixa">
        <LoadingOverlay message="Carregando…" />
      </Shell>
    );
  }

  const entered = closingTouched;
  const openedAt = moment(session.getOpenedAt());
  const openedAtLabel = openedAt.format("DD/MM/YYYY [às] HH:mm");
  const daysOpen = Math.max(1, moment().diff(openedAt, "days"));

  return (
    <Shell title="Fechar caixa">
      {submitting ? <LoadingOverlay subtle message="Fechando caixa…" /> : null}

      <Stack spacing={3}>
        {/* ── Alerta de fechamento forçado ── */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1.5px solid ${alpha(theme.palette.warning.main, 0.45)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.07)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 1.25,
              background: alpha(theme.palette.warning.main, 0.12),
              borderBottom: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <TimerOffIcon sx={{ color: "warning.dark", fontSize: 18 }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: "warning.dark", letterSpacing: "0.03em" }}>
              Fechamento obrigatório
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} alignItems={{ sm: "flex-start" }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.22)}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <LockIcon sx={{ color: "warning.dark", fontSize: 24 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} color="warning.dark" sx={{ mb: 0.5 }}>
                  Este caixa está aberto há{" "}
                  {daysOpen === 1 ? "mais de 1 dia" : `${daysOpen} dias`} sem fechamento
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
                  O caixa foi aberto em{" "}
                  <strong style={{ color: theme.palette.text.primary }}>{openedAtLabel}</strong> e
                  não foi fechado ao final do expediente. Para manter a integridade financeira,
                  o fechamento precisa ser realizado antes de qualquer nova operação.
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  <Chip
                    icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 14 }} />}
                    label={`Aberto em ${openedAtLabel}`}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.warning.main, 0.28)}`,
                      color: "warning.dark",
                      "& .MuiChip-icon": { color: "warning.dark" },
                    }}
                  />
                  <Chip
                    label={daysOpen === 1 ? "Virou o dia sem fechamento" : `${daysOpen} dias sem fechamento`}
                    size="small"
                    sx={{
                      fontWeight: 500,
                      bgcolor: alpha(theme.palette.warning.main, 0.07),
                      border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                      color: "warning.dark",
                    }}
                  />
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Paper>

        {/* ── Confirmar fechamento (topo) ── */}
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <MonetizationOnOutlinedIcon sx={{ color: "primary.main", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
              Conferência do caixa físico
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Informe o valor em espécie contado fisicamente na gaveta
            </Typography>
          </Box>
        </Stack>

        <Card
          elevation={0}
          sx={{
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Stack spacing={3}>
              {/* Input principal */}
              <MoneyInput
                title="Dinheiro contado"
                value={closingValue}
                onChange={({ value }) => {
                  setClosingValue(value);
                  setClosingTouched(true);
                }}
                textFieldProps={{
                  disabled: !canManage,
                  sx: {
                    maxWidth: 360,
                    "& .MuiInputBase-input": { fontSize: "1.5rem", fontWeight: 700, letterSpacing: "-0.01em" },
                  },
                }}
              />

              <Divider />

              {/* Rodapé: responsável + botão */}
              <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} justifyContent="space-between" spacing={2}>
                <Chip
                  icon={<PersonOutlineIcon sx={{ fontSize: 16 }} />}
                  label={`Responsável: ${user?.name ?? "—"}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 500, color: "text.secondary", borderColor: "divider" }}
                />
                <Button
                  variant="contained"
                  size="large"
                  disabled={!canManage || submitting}
                  onClick={onSubmit}
                  startIcon={<CheckCircleOutlineIcon />}
                  sx={{ minWidth: 220, borderRadius: 2 }}
                >
                  Confirmar fechamento
                </Button>
              </Stack>

              {!canManage && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  Você não tem permissão para fechar o caixa. Peça a alguém com{" "}
                  <strong>gestão de caixa</strong> para concluir o fechamento.
                </Alert>
              )}

              {error && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {error}
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Divider />

        {/* ── Hero ── */}
        <CashRegisterReportHero
          overline="Resumo da sessão"
          title="Conferência de caixa"
          chips={
            <>
              <Chip
                icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />}
                label={`Aberto em ${openedAtLabel}`}
                size="small"
                sx={{
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.warning.main, 0.12),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.28)}`,
                  color: "warning.dark",
                  "& .MuiChip-icon": { color: "warning.dark" },
                }}
              />
              <Chip
                label="Caixa expirado · virou o dia"
                size="small"
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            </>
          }
        />

        {/* ── Stat cards ── */}
        {previewSummary && (
          <Grid container spacing={2}>
            {/* Linha 1: visão geral */}
            <Grid item xs={12} sm={4}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.background.paper, 1)} 60%)`,
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Abertura do caixa
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, letterSpacing: "-0.02em" }}>
                        {money(previewSummary.openingCash)}
                      </Typography>
                    </Box>
                    <LockOpenIcon sx={{ color: "primary.main", opacity: 0.85 }} />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  background: `linear-gradient(145deg, ${alpha(theme.palette.success.main, 0.09)} 0%, ${alpha(theme.palette.background.paper, 1)} 60%)`,
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Total vendido
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, letterSpacing: "-0.02em" }}>
                        {money(previewSummary.totalSales)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                        {previewSummary.countCompletedSales} vendas concluídas
                      </Typography>
                    </Box>
                    <TrendingUpIcon sx={{ color: "success.main", opacity: 0.85 }} />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                  background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.background.paper, 1)} 60%)`,
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Esperado em caixa
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, letterSpacing: "-0.02em", color: "primary.dark" }}>
                        {money(previewSummary.expectedCashApprox)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                        Abertura + entradas − retiradas + dinheiro
                      </Typography>
                    </Box>
                    <AccountBalanceWalletOutlinedIcon sx={{ color: "primary.main", opacity: 0.85 }} />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Linha 2: detalhamento do fluxo de caixa */}
            <Grid item xs={12} sm={4}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  background: `linear-gradient(145deg, ${alpha(theme.palette.success.main, 0.06)} 0%, ${alpha(theme.palette.background.paper, 1)} 60%)`,
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Vendas em dinheiro
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, letterSpacing: "-0.02em" }}>
                        {money(previewSummary.totalCashSales)}
                      </Typography>
                    </Box>
                    <PointOfSaleOutlinedIcon sx={{ color: "success.main", opacity: 0.75 }} />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  background: `linear-gradient(145deg, ${alpha(theme.palette.info.main, 0.08)} 0%, ${alpha(theme.palette.background.paper, 1)} 60%)`,
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Entradas manuais
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, letterSpacing: "-0.02em" }}>
                        {money(previewSummary.totalEntries)}
                      </Typography>
                    </Box>
                    <ArrowUpwardIcon sx={{ color: "info.main", opacity: 0.85 }} />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  background: `linear-gradient(145deg, ${alpha(theme.palette.error.main, 0.07)} 0%, ${alpha(theme.palette.background.paper, 1)} 60%)`,
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Retiradas
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, letterSpacing: "-0.02em", color: previewSummary.totalWithdrawals > 0 ? "error.main" : "text.primary" }}>
                        {money(previewSummary.totalWithdrawals)}
                      </Typography>
                    </Box>
                    <ArrowDownwardIcon sx={{ color: "error.main", opacity: 0.75 }} />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* ── Relatório de vendas ── */}
        {previewSummary && (
          <Card elevation={0}>
            <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Relatório de vendas
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Resumo por forma de pagamento e conferência do saldo em dinheiro.
              </Typography>
            </Box>
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              <CashRegisterReportView
                summary={previewSummary}
                title=""
                showHealth={entered}
              />
            </CardContent>
          </Card>
        )}

        {/* ── Log de movimentações ── */}
        <Card elevation={0}>
          <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: 1, borderColor: "divider" }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Log de movimentações
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Histórico de entradas, retiradas e vendas em dinheiro desta sessão.
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <CashMovementsLogView
              openingCash={session.getOpeningCash()}
              movements={movements}
              sells={sells}
            />
          </CardContent>
        </Card>

        <Box sx={{ pb: 2 }} />
      </Stack>
    </Shell>
  );
}
